<template>
  <div id="acctLogin">
    <div class="container">
      <h1>Account Login</h1>
      <div class="row">
        <div class="col-lg-6 mb-4 mb-lg-0">
          <form
            id="loginForm"
            @submit.prevent="login"
            v-bind:class="{ error: error }"
          >
            <div class="ui error message" v-if="error">{{ error }}</div>
            <div class="form-row">
              <label for="inputEmail">Email</label>
              <input
                v-model="email"
                type="email"
                id="inputEmail"
                placeholder="Email address"
                required
                autofocus
              />
            </div>
            <div class="form-row">
              <label for="inputPassword">Password</label>
              <input
                v-model="password"
                type="password"
                id="inputPassword"
                placeholder="Password"
                required
              />
            </div>
            <div class="form-row align-items-center">
              <button
                class="ui btn-primary col-sm-6 mb-4 mb-sm-0"
                type="submit"
              >
                Log into your account
              </button>
              <router-link
                to="/ResetPassword"
                tag="a"
                class="forgot-pw col-sm-6 text-sm-right"
                >forgot your password?</router-link
              >
            </div>
          </form>
          <div class="ms-signin-wrap">
            <div class="ms-or"><span>or</span></div>
            <button type="button" class="ms-signin-btn" :disabled="msBusy" @click="loginWithMicrosoft">
              <svg class="ms-logo" viewBox="0 0 21 21" aria-hidden="true">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              <span>{{ msBusy ? "Signing in…" : "Sign in with Microsoft" }}</span>
            </button>
            <p class="ms-hint">For JC Steele staff — use your @jcsteele.com account</p>
          </div>
        </div>
        <div class="col-lg-6 align-items-center">
          <div id="createAcct">
            <h2>Create A New Account</h2>
            <p>
              J.C. Steele is proud to introduce the new Steele Service, a database-driven
              web component designed to be the foundation of future e-commerce
              phases. The new Steele Service website is an online order entry system
              for J.C. Steele's existing customers.
            </p>
            <router-link
              to="/Registration"
              tag="a"
              class="col-sm-6 btn btn-primary"
              >Request new account</router-link
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import "expose-loader?$!expose-loader?jQuery!jquery";

import APIService from "@/assets/js/APIService";
import { signInWithMicrosoft } from "@/assets/js/msalAuth";
const apiService = new APIService();

export default {
  name: "Login",
  data() {
    return {
      email: "",
      password: "",
      error: false,
      msBusy: false,
      loader: this.loader
    };
  },
  computed: {
    ...mapGetters({ currentUser: "currentUser" })
  },
  created() {
    this.checkCurrentLogin();
  },
  updated() {
    this.checkCurrentLogin();
  },
  methods: {
    checkCurrentLogin() {
      if (this.currentUser) {
        // Only honor a RELATIVE redirect (starts with a single "/") — an
        // absolute or protocol-relative value in ?redirect= would be an
        // open-redirect hole. The router guards set this to to.fullPath,
        // which is always relative; anything else falls back to the
        // role-aware landing route.
        var r = this.$route.query.redirect;
        var safe = r && String(r).charAt(0) === "/" && String(r).charAt(1) !== "/";
        this.$router.replace(safe ? String(r) : this.landingRoute());
      }
    },
    // Where to land after login. Sales-role staff have no company, so /account/index is blank for
    // them — send them to the REPORTS HOME (user 2026-07-31; was the Sales Insights report directly):
    // one place with every report they can open plus the What's New feed.
    landingRoute() {
      const r = this.currentUser && this.currentUser.role;
      const roles = (Array.isArray(r) ? r : [r]).map((x) => String(x || "").toLowerCase());
      const has = (name) => roles.indexOf(name.toLowerCase()) !== -1;
      if (has("Sales") && !has("Master") && !has("Sales All") && !has("Customer")) {
        return "/reports/reports-dashboard";
      }
      return "/account/index";
    },
    login() {
      this.loader = true;
      apiService
        .login({
          Email: this.email,
          Password: this.password
        })
        .then(request => {
          this.loginSuccessful(request);
        })
        .catch(err => this.loginFailed(err));
    },
    loginSuccessful(req) {
      if (!req.token) {
        this.loginFailed();
        return;
      }
      this.loader = false;
      this.error = false;
      localStorage.token = req.token;
      this.$store.dispatch("login");
      this.$router.go();
    },
    loginFailed(err) {
      this.loader = false;
      // Prefer the server's specific reason (e.g. "hasn't been approved yet") so we
      // don't send the customer down the wrong path with the generic message.
      const data = err && err.error && err.error.response && err.error.response.data;
      let msg = "";
      if (data) {
        msg = typeof data === "string" ? data : (data.message || data.detail || "");
      }
      this.error = msg || "Login failed! Email or Password does not match our records.";
      this.$store.dispatch("logout");
      delete localStorage.token;
    },
    async loginWithMicrosoft() {
      this.error = false;
      this.msBusy = true;
      try {
        const idToken = await signInWithMicrosoft();
        const req = await apiService.microsoftLogin(idToken);
        this.loginSuccessful(req); // stores token + dispatch("login") + router.go() — same path as password login
      } catch (err) {
        this.msBusy = false;
        console.error("[MS sign-in] failed:", err, err && err.response, err && err.response && err.response.data);
        // User just closed/cancelled the Microsoft popup — not an error worth showing.
        const blob = (
          (err && (err.errorCode || err.name || "")) + " " +
          ((err && (err.errorMessage || err.message)) || "")
        ).toLowerCase();
        if (/user_cancelled|user_cancel|popup_window|cancelled|interaction_in_progress/.test(blob)) return;
        // Surface the backend's reason (e.g. only @jcsteele.com) when present.
        const resp = err && (err.response || (err.error && err.error.response));
        const data = resp && resp.data;
        const apiMsg = data && (typeof data === "string" ? data : data.message);
        this.error = apiMsg || "Microsoft sign-in didn't work. Please try again, or use your email and password.";
        this.$store.dispatch("logout");
        delete localStorage.token;
      }
    }
  }
};
</script>

<style>
/* ===== Sign in with Microsoft (SSO for @jcsteele.com staff) ===== */
.ms-signin-wrap { margin-top: 18px; }
.ms-or { display: flex; align-items: center; text-align: center; color: #999; font-size: 12px; margin: 14px 0; }
.ms-or::before, .ms-or::after { content: ""; flex: 1; border-bottom: 1px solid #e0e0e0; }
.ms-or span { padding: 0 10px; text-transform: uppercase; letter-spacing: .06em; }
.ms-signin-btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; width: 100%; max-width: 320px; padding: 10px 14px; background: #fff !important; border: 1px solid #8c8c8c !important; border-radius: 4px; color: #5e5e5e !important; font-size: 15px; font-weight: 600; cursor: pointer; transition: background .12s, box-shadow .12s; }
.ms-signin-btn span { color: inherit !important; }
.ms-signin-btn:hover:not(:disabled) { background: #f3f3f3 !important; box-shadow: 0 1px 4px rgba(0,0,0,.15); }
.ms-signin-btn:disabled { opacity: .6; cursor: default; }
.ms-logo { width: 20px; height: 20px; flex: none; }
.ms-hint { font-size: 11.5px; color: #888; margin: 8px 0 0; }
/* Mid / dark themes: switch to Microsoft's dark button (dark bg, white text) so the label stays readable. */
html.mid-mode .ms-signin-btn, html.dark-mode .ms-signin-btn { background: #2b2b2b !important; color: #fff !important; border-color: #5a5a5a !important; }
html.mid-mode .ms-signin-btn:hover:not(:disabled), html.dark-mode .ms-signin-btn:hover:not(:disabled) { background: #3a3a3a !important; }
html.mid-mode .ms-hint, html.dark-mode .ms-hint, html.mid-mode .ms-or, html.dark-mode .ms-or { color: #b9b9b9 !important; }

/* ===== LOGIN PAGE LAYOUT FIXES =====
   These rules use #acctLogin so they target only the login page.
   Not scoped because the global stylesheet's wider rules (notably
   the white background that extends past the brushed-steel rails)
   are applied via global selectors and can't be overridden from
   inside a scoped block without piercing.

   Three goals:
   1. Kill the full-width white band that bleeds past the rails.
      The page sits inside a parent that paints a white background
      across the full viewport; we make the login wrapper itself
      transparent and constrain its visible card to the container
      width so the rails stay visible at the sides.
   2. Pull the content up — the user reported a tall empty white
      area between the header and the form. Reducing the top
      padding moves the form card up under the header.
   3. Wrap the form + create-account split into a single white
      panel that sits centered on the page rather than stretching
      shoulder to shoulder. */

#acctLogin {
  background: transparent;
  padding-top: 0;
  margin-top: 0;
}

#acctLogin > .container {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  padding: 28px 32px;
  margin-top: 24px;
  margin-bottom: 24px;
  max-width: 1100px;
}

#acctLogin h1 {
  margin-top: 0;
  margin-bottom: 20px;
}

#acctLogin #createAcct {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

/* Override any inherited body/html-level background that paints
   white across the full viewport behind the login page. The
   .auth-shell wrapper (or whatever the parent provides) should
   stay transparent so the brushed-steel rails render. */
body.login-page,
html.login-page,
#app:has(#acctLogin) {
  background: transparent;
}
</style>